import type { SocialPlatform } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Loader2, type LucideIcon, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useConnections } from '~/entities/crossposting/model/use-connections'
import { AiImageButton, AiTextButton } from '~/features/ai-studio/components'
import { ImageCropFrame } from '~/features/crossposting/components/image-crop-frame'
import { cropFileToFormat, ImageCropError } from '~/features/crossposting/lib/crop-image'
import { getPostFormat } from '~/features/crossposting/lib/formats'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Skeleton } from '~/shared/ui/skeleton'
import { Textarea } from '~/shared/ui/textarea'
import { PageHeader } from '~/widgets/page-header'

export interface ComposerType {
  value: string
  label: () => string
  hint: () => string
  disabled?: boolean
  maxImages: number
  mediaRequired: boolean
  showLink?: boolean
}

export interface ComposerConfig {
  platform: SocialPlatform
  title: () => string
  description: () => string
  publishLabel: () => string
  publishedLabel: () => string
  icon: LucideIcon
  types: ComposerType[]
}

interface ComposerOutput {
  type: string
  text: string
  link: string
  files: File[]
}

interface SelectedImage {
  file: File
  preview: string
  /** Position of the crop frame along the image's slack axis, 0..1. */
  offset: number
}

export function SocialPostComposer({
  listingId,
  config,
  onPublish,
}: {
  listingId: string
  config: ComposerConfig
  onPublish: (data: ComposerOutput) => Promise<void>
}) {
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: connectionData, isLoading: isConnectionsLoading } = useConnections(
    businessId,
    listingId,
  )

  const firstType = config.types.find((t) => !t.disabled)?.value ?? config.types[0]?.value ?? 'post'
  const [type, setType] = useState(firstType)
  const [text, setText] = useState('')
  const [link, setLink] = useState('')
  const [images, setImages] = useState<SelectedImage[]>([])
  const [publishing, setPublishing] = useState(false)
  const captionFieldId = useId()
  const linkFieldId = useId()

  const imagesRef = useRef(images)
  imagesRef.current = images
  useEffect(() => {
    return () => {
      for (const image of imagesRef.current) URL.revokeObjectURL(image.preview)
    }
  }, [])

  const activeType = config.types.find((t) => t.value === type) ?? config.types[0]
  const maxImages = activeType?.maxImages ?? 1
  const format = getPostFormat(config.platform, type)

  const addImage = (file: File) => {
    const preview = URL.createObjectURL(file)
    setImages((prev) => {
      if (maxImages === 1) {
        for (const image of prev) URL.revokeObjectURL(image.preview)
        return [{ file, preview, offset: 0.5 }]
      }
      if (prev.length >= maxImages) {
        URL.revokeObjectURL(preview)
        toast.error(m.crosspostingMaxImages({ count: String(maxImages) }))
        return prev
      }
      return [...prev, { file, preview, offset: 0.5 }]
    })
  }

  const setImageOffset = (index: number, offset: number) => {
    setImages((prev) => prev.map((image, i) => (i === index ? { ...image, offset } : image)))
  }

  const removeImage = (index: number) => {
    setImages((prev) => {
      const target = prev[index]
      if (target) URL.revokeObjectURL(target.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const selectType = (next: ComposerType) => {
    if (next.disabled) return
    setType(next.value)
    if (next.maxImages < images.length) {
      setImages((prev) => {
        for (const image of prev.slice(next.maxImages)) URL.revokeObjectURL(image.preview)
        return prev.slice(0, next.maxImages)
      })
    }
  }

  const reset = () => {
    for (const image of images) URL.revokeObjectURL(image.preview)
    setText('')
    setLink('')
    setImages([])
  }

  const hasContent = activeType?.mediaRequired
    ? images.length > 0
    : Boolean(text.trim() || (activeType?.showLink && link.trim()) || images.length > 0)

  const publish = async () => {
    setPublishing(true)
    try {
      const files = await Promise.all(
        images.map((image) => cropFileToFormat(image.file, image.preview, format, image.offset)),
      )

      await onPublish({
        type,
        text,
        link: activeType?.showLink ? link : '',
        files,
      })
      toast.success(config.publishedLabel())
      reset()
    } catch (error) {
      toast.error(
        error instanceof ImageCropError
          ? m.crosspostingImageUnprocessable()
          : getErrorMessageFromError(error),
      )
    } finally {
      setPublishing(false)
    }
  }

  const backLink = (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <Link to="/dashboard/crossposting/$listingId" params={{ listingId }}>
        <ArrowLeft className="h-4 w-4" />
        {m.crosspostingBackToTools()}
      </Link>
    </Button>
  )

  if (isBusinessLoading || isConnectionsLoading) {
    return (
      <div className="space-y-6">
        {backLink}
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    )
  }

  const connected =
    connectionData?.connections.find((c) => c.platform === config.platform)?.status === 'connected'

  if (!connected) {
    return (
      <div className="space-y-6">
        {backLink}
        <PageHeader title={config.title()} description={config.description()} />
        <Card className="flex flex-col items-start gap-3 p-6">
          <p className="text-muted-foreground text-sm">{m.crosspostingNotConnectedPlatform()}</p>
          <Button asChild>
            <Link to="/dashboard/crossposting/$listingId/connect" params={{ listingId }}>
              {m.crosspostingConnect()}
            </Link>
          </Button>
        </Card>
      </div>
    )
  }

  const Icon = config.icon

  return (
    <div className="space-y-6">
      {backLink}
      <PageHeader title={config.title()} description={config.description()} />

      <Card className="space-y-5 p-6">
        <div className="space-y-2">
          <Label>{m.crosspostingPostType()}</Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {config.types.map((option) => {
              const isActive = !option.disabled && option.value === type
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => selectType(option)}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-colors',
                    isActive
                      ? 'border-brand bg-brand-subtle text-brand-subtle-foreground'
                      : 'hover:bg-accent',
                    option.disabled && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{option.label()}</span>
                    {option.disabled && (
                      <Badge variant="secondary">{m.crosspostingComingSoonTitle()}</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-muted-foreground text-xs">{option.hint()}</p>
                </button>
              )
            })}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={captionFieldId}>{m.crosspostingCaption()}</Label>
            <AiTextButton kind={`${config.platform}-post`} onUse={setText} />
          </div>
          <Textarea
            id={captionFieldId}
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={m.crosspostingCaptionPlaceholder()}
          />
        </div>

        {activeType?.showLink && (
          <div className="space-y-2">
            <Label htmlFor={linkFieldId}>{m.crosspostingLink()}</Label>
            <Input
              id={linkFieldId}
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://…"
            />
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{m.crosspostingImages()}</Label>
            <AiImageButton onUse={addImage} orientation="portrait" />
          </div>

          {images.length > 0 && (
            <div className="space-y-3">
              {images.map((image, index) => (
                <div key={image.preview} className="group relative">
                  <ImageCropFrame
                    previewUrl={image.preview}
                    format={format}
                    offset={image.offset}
                    onOffsetChange={(offset) => setImageOffset(index, offset)}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute top-1 right-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {images.length < maxImages && (
            <ImageUploadField
              file={null}
              preview={null}
              compact
              onSelect={(file) => file && addImage(file)}
              subHint={m.crosspostingMaxImages({ count: String(maxImages) })}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">{activeType?.hint()}</p>
          <Button onClick={publish} disabled={!hasContent || publishing}>
            {publishing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
            {config.publishLabel()}
          </Button>
        </div>
      </Card>
    </div>
  )
}
